import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Zap, Hand, Loader2 } from 'lucide-react';
import { getGoogleIntegrationStatus, toggleAutoSync } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const STORAGE_PREFIX = 'quantro_sync_pref_asked_';

/**
 * One-time prompt shown right after a Google connection goes live:
 * "keep syncing automatically every 15 min, or only when I ask?".
 *
 * Why this exists: the first import auto-classifies everything (up to
 * 200 messages) with no way to opt out — that's an explicit,
 * one-time action the user just took. Every sync AFTER that also
 * auto-classifies new mail, which spends AI credits with no per-run
 * confirmation. Letting the user choose "manual" here means they
 * control exactly when that credit spend happens, without adding a
 * confirmation step to every single sync.
 *
 * Persistence is intentionally client-side only (localStorage, keyed
 * per workspace via the Google account email we already have):
 * skipping this once-per-connection prompt again is low-stakes, and
 * it avoids a backend schema change for what's really just "have we
 * already asked in this browser".
 */
export default function SyncPreferenceDialog() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [statusKey, setStatusKey] = useState(null);
  const [saving, setSaving] = useState(null); // 'auto' | 'manual' | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await getGoogleIntegrationStatus();
        if (cancelled) return;
        const isHealthy = status?.connected && status?.status === 'ok' && !status?.reauthorization_required;
        if (!isHealthy) return;
        const key = STORAGE_PREFIX + (status.account_email || 'default');
        setStatusKey(key);
        if (!localStorage.getItem(key)) {
          setOpen(true);
        }
      } catch (_) {
        // Not connected / not authenticated yet — nothing to prompt.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const choose = async (preference) => {
    setSaving(preference);
    try {
      await toggleAutoSync('google', preference === 'manual');
      if (statusKey) localStorage.setItem(statusKey, '1');
      toast.success(
        preference === 'auto'
          ? t('sync_preference.toast_auto')
          : t('sync_preference.toast_manual')
      );
      setOpen(false);
    } catch (err) {
      toast.error(t('sync_preference.toast_error'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) setOpen(v); }}>
      <DialogContent data-testid="sync-preference-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('sync_preference.title')}</DialogTitle>
          <DialogDescription>{t('sync_preference.description')}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 py-2">
          <button
            type="button"
            data-testid="sync-preference-auto-btn"
            onClick={() => choose('auto')}
            disabled={!!saving}
            className="flex items-start gap-3 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60"
          >
            <Zap size={20} className="mt-0.5 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">{t('sync_preference.auto_title')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('sync_preference.auto_desc')}</p>
            </div>
            {saving === 'auto' && <Loader2 size={16} className="ml-auto animate-spin text-muted-foreground" />}
          </button>
          <button
            type="button"
            data-testid="sync-preference-manual-btn"
            onClick={() => choose('manual')}
            disabled={!!saving}
            className="flex items-start gap-3 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60"
          >
            <Hand size={20} className="mt-0.5 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">{t('sync_preference.manual_title')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('sync_preference.manual_desc')}</p>
            </div>
            {saving === 'manual' && <Loader2 size={16} className="ml-auto animate-spin text-muted-foreground" />}
          </button>
        </div>
        <DialogFooter>
          <p className="text-xs text-muted-foreground">{t('sync_preference.footer_hint')}</p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
